// Desktop-sidebar collapse state. Mirrors `theme.tsx`: in-memory state +
// localStorage persistence + sensible defaults. Mobile is unaffected — the
// app-shell mounts the Sheet drawer separately and ignores this flag.
//
// Default = expanded so first-visit users discover the nav with labels.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface SidebarStateValue {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (next: boolean) => void;
}

const STORAGE_KEY = 'cortex-sidebar-collapsed';

const SidebarStateContext = createContext<SidebarStateValue | null>(null);

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // localStorage may be unavailable (private mode, file://). Fall through
    // to the in-memory default rather than refusing to render.
    return false;
  }
}

// Tags used by the keyboard shortcut handler to skip toggling while the user
// is typing. Includes the editable-content escape hatch (Slate, prose-mirror)
// so a tagged textarea inside a custom editor isn't punched through.
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function SidebarStateProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setCollapsedState] = useState<boolean>(() => readStored());

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // ignore — see readStored() comment.
    }
  }, []);

  const toggle = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // Two keybindings, both toggle the sidebar:
  //   • Cmd/Ctrl+B — primary, conventional across VS Code / Linear / Notion /
  //     Slack / Cursor. The one users actually try first.
  //   • [ — kept for muscle memory from the original Linear-style binding.
  // Both skip when the user is typing in any editable element (input,
  // textarea, select, contentEditable) so they don't punch through field
  // input. Cmd/Ctrl+B in particular collides with browser "show bookmarks" /
  // "bold" defaults in editable contexts; the typing guard handles both.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const isModB = (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey
        && (e.key === 'b' || e.key === 'B');
      const isBracket = e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (!isModB && !isBracket) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [toggle]);

  const value = useMemo<SidebarStateValue>(
    () => ({ isCollapsed, toggle, setCollapsed }),
    [isCollapsed, toggle, setCollapsed],
  );

  return (
    <SidebarStateContext.Provider value={value}>{children}</SidebarStateContext.Provider>
  );
}

// Defaults to "expanded, no-op setters" if no provider is mounted (matches
// the theme provider's pattern so unit tests rendering individual layout
// pieces don't have to wrap with the provider).
const NOOP_SIDEBAR: SidebarStateValue = {
  isCollapsed: false,
  toggle: () => {
    /* no-op outside provider */
  },
  setCollapsed: () => {
    /* no-op outside provider */
  },
};

export function useSidebarState(): SidebarStateValue {
  return useContext(SidebarStateContext) ?? NOOP_SIDEBAR;
}
